import type {
  ComboboxOption,
  ComboboxOptions
} from '@library/components/selects';
import { useCallback, useMemo, useState } from 'react';
import { useGetAdminUsersQuery } from '@bo-services/userApi';
import { useUpdateMemberMutation } from '@bo-services/members/membersApi';

export interface MemberVipStatusProps {
  isVipMember: boolean;
  vipHostAssignedId?: string;
}

export const useMemberVipStatus = ({
  isVipMember,
  vipHostAssignedId
}: MemberVipStatusProps) => {
  const [isVipStatusActive, setIsVipStatusActive] = useState(isVipMember);
  const [assignedVipHost, setAssignedVipHost] = useState<ComboboxOption | null>(
    null
  );
  const [selectedVipHost, setSelectedVipHost] = useState<ComboboxOption | null>(
    null
  );

  const { data: adminUsersData, isFetching: isVipHostsFetching } =
    useGetAdminUsersQuery({
      vip_host: true
    });

  const [
    updateMember,
    {
      isLoading: isSubmitLoading,
      isError: isSubmitError,
      isSuccess: isSubmitSuccess
    }
  ] = useUpdateMemberMutation();

  const checkForAssignedVipHost = useCallback(
    (options: ComboboxOptions) => {
      if (vipHostAssignedId && options?.length) {
        const vipHost =
          options.find((option) => option.value === vipHostAssignedId) ?? null;
        setAssignedVipHost(vipHost);
        setSelectedVipHost(vipHost);
      }
    },
    [vipHostAssignedId]
  );

  const vipHostComboboxOptions = useMemo(() => {
    const options = adminUsersData?.users.map((vipHost) => ({
      label: String(`${vipHost.first_name} ${vipHost.last_name}`),
      value: vipHost.id
    }));

    const sortedOptions = options?.toSorted((a, b) =>
      a.label.localeCompare(b.label)
    ) as ComboboxOptions;

    checkForAssignedVipHost(sortedOptions);

    return sortedOptions;
  }, [adminUsersData, checkForAssignedVipHost]);

  const handleVipHostChange = useCallback(
    (selectedOption: ComboboxOption | null) => {
      setSelectedVipHost(selectedOption);
    },
    []
  );

  const handleVipStatusChange = useCallback(() => {
    setIsVipStatusActive((prev) => {
      const newValue = !prev;
      // Reset selected vip host to current/assigned host if vip status is disabled
      if (!newValue) {
        setSelectedVipHost(assignedVipHost);
      }
      return newValue;
    });
  }, [assignedVipHost]);

  const handleSubmit = useCallback(
    async (memberId: string) => {
      await updateMember({
        member_id: memberId,
        vmp_member: isVipStatusActive,
        vip_host_id: isVipStatusActive ? selectedVipHost?.value : undefined
      }).unwrap();
    },
    [isVipStatusActive, selectedVipHost, updateMember]
  );

  return {
    isLoading: isVipHostsFetching || isSubmitLoading,
    isSubmitError,
    isSubmitSuccess,
    isVipStatusActive,
    onSubmit: handleSubmit,
    onVipHostChange: handleVipHostChange,
    onVipStatusChange: handleVipStatusChange,
    selectedVipHost,
    vipHostComboboxOptions
  };
};
